import { useState } from 'react';
import { useAction, useApi } from '../../api/hooks.js';
import {
  decideReview,
  getOpenHits,
  getReviewNext,
  getReviewQueue,
  getSubjectDecisions,
  type DecisionResult,
  type ReviewTask,
} from '../api.js';
import { REVIEW_SEVERITIES, type ReviewDecision } from '../forms.js';
import { decisionLabel, fmtWhen, humanise, severityLabel, shortId, statusLabel } from '../labels.js';
import { adminHash } from '../routes.js';
import { Card, Empty, Head, IdChip, Lanes, Loading, severityTone, show, Tag, Trouble } from '../ui.js';

/**
 * The review lane. Since 2026-08-21 the person in the photo decides their own
 * matches; the console is the override path, and staff never see match
 * imagery. So the task is shown as details, not pictures: the image address
 * is text — never a link, never an image, never fetched — and the face
 * position is numbers.
 *
 * "Confirm" is the highest-consequence action in the product: it is what
 * makes a match visible to the person. It takes two clicks here, the second
 * one naming the consequence. Reject and Uncertain take one.
 */

const PAGE_SIZES = [50, 100, 200, 500] as const;
const DEFAULT_PAGE = 50;

/** Triage keys, as a reader would call them. Unknown keys read as sentence case. */
const TRIAGE_LABELS: Record<string, string> = {
  best_face_bbox: 'Face position (x, y, width, height)',
  face_match_score: 'Face match score',
  moderation_labels: 'Content labels',
  phash_degenerate: 'Duplicate check inconclusive',
  skipped: 'Skipped',
  unfetchable: 'Could not be fetched',
};

export function ReviewScreen() {
  const next = useApi(getReviewNext, []);
  const queue = useApi(getReviewQueue, []);
  const [limit, setLimit] = useState<number>(DEFAULT_PAGE);
  const open = useApi(() => getOpenHits(limit), [limit]);
  const decided = useApi(() => getSubjectDecisions(limit), [limit]);
  const [last, setLast] = useState<DecisionResult | null>(null);

  const decide = useAction(
    (taskId: string, decision: ReviewDecision, severity?: string) => decideReview(taskId, decision, severity),
    (result) => {
      setLast(result);
      next.reload();
      queue.reload();
      open.reload();
    },
  );

  // The normal two-operators race: the task is no longer pending because
  // somebody else decided it, or it was quarantined. Not a failure — move on.
  const raced = decide.error !== null && decide.error.code === 'REVIEW_TASK_NOT_FOUND';

  return (
    <>
      <Head
        title="Review"
        lede="Matches that need a human decision, most serious first. Only a decision here — or the person's own — makes a match visible to them. Choosing Uncertain leaves it in the queue; nothing is decided automatically."
      />

      <div className="cr-grid-2">
        <Card
          title="Needs a decision"
          aside={
            <button type="button" className="cr-btn ghost" onClick={next.reload} disabled={next.loading}>
              {next.loading ? 'Loading…' : 'Refresh'}
            </button>
          }
        >
          {raced && (
            <div className="cr-notice" role="status">
              <strong>Someone else decided that one first.</strong>
              <span>The task was no longer waiting, so the next one has been loaded.</span>
            </div>
          )}
          {last !== null && (
            <div className="cr-notice ok" role="status">
              <strong>Recorded as {decisionLabel(last.decision).toLowerCase()}.</strong>
              <span>
                Match {shortId(last.infringement_id)} · severity {severityLabel(last.severity)}
              </span>
            </div>
          )}
          {next.error !== null ? (
            <Trouble error={next.error} />
          ) : next.loading && next.data === null ? (
            <Loading />
          ) : next.data === null ? (
            <Empty>Nothing is waiting for a decision right now.</Empty>
          ) : (
            <TaskCard
              task={next.data}
              pending={decide.pending}
              onDecide={(decision, severity) => {
                if (next.data !== null) decide.run(next.data.task_id, decision, severity);
              }}
            />
          )}
          {decide.error !== null && !raced && <Trouble error={decide.error} />}
        </Card>

        <Card title="Queue by severity">
          {queue.error !== null ? (
            <Trouble error={queue.error} />
          ) : queue.data === null ? (
            <Loading />
          ) : (
            <>
              <Lanes queue={queue.data} />
              <p className="cr-fine" style={{ marginTop: 12 }}>
                The most serious lane is always served first; within a lane, the oldest match first.
              </p>
            </>
          )}
        </Card>
      </div>

      <Card
        title="Waiting on the person"
        aside={
          <label className="cr-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <span>Show</span>
            <select value={limit} onChange={(event) => setLimit(Number(event.target.value))}>
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        }
      >
        <p className="cr-card-note">Matches waiting for the person's own answer — is this you, or not.</p>
        {open.error !== null ? (
          <Trouble error={open.error} />
        ) : open.data === null ? (
          <Loading />
        ) : open.data.hits.length === 0 ? (
          <Empty>No matches are waiting on an answer.</Empty>
        ) : (
          <div className="cr-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Found</th>
                  <th>Person</th>
                  <th>Status</th>
                  <th>Severity</th>
                  <th>Website</th>
                  <th>Match</th>
                </tr>
              </thead>
              <tbody>
                {open.data.hits.map((hit) => (
                  <tr key={hit.infringement_id}>
                    <td>{fmtWhen(hit.first_seen_at)}</td>
                    <td>
                      <IdChip value={hit.user_ref} href={adminHash('scores', hit.user_ref)} what="person ID" />
                    </td>
                    <td>
                      <Tag>{statusLabel(hit.confirm_state)}</Tag>
                    </td>
                    <td>
                      <Tag tone={severityTone(hit.severity)}>{severityLabel(hit.severity)}</Tag>
                    </td>
                    <td>{hit.source_domain}</td>
                    <td>
                      <IdChip value={hit.infringement_id} what="match ID" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Decided by the person">
        <p className="cr-card-note">
          The answers people gave on their own matches. Confirmed explicit matches are candidates for a
          takedown campaign.
        </p>
        {decided.error !== null ? (
          <Trouble error={decided.error} />
        ) : decided.data === null ? (
          <Loading />
        ) : decided.data.decisions.length === 0 ? (
          <Empty>No decisions from people yet.</Empty>
        ) : (
          <div className="cr-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Person</th>
                  <th>Decision</th>
                  <th>Severity</th>
                  <th>Website</th>
                  <th>Match</th>
                </tr>
              </thead>
              <tbody>
                {decided.data.decisions.map((row, index) => {
                  const explicit = row.severity === 'ncii_suspected' || row.severity === 'explicit_unmatched';
                  return (
                    <tr
                      key={`${row.infringement_id}-${String(index)}`}
                      className={explicit && row.decision === 'confirmed' ? 'flag' : undefined}
                    >
                      <td>{fmtWhen(row.occurred_at)}</td>
                      <td>
                        <IdChip value={row.user_ref} href={adminHash('scores', row.user_ref)} what="person ID" />
                      </td>
                      <td>
                        <Tag tone={row.decision === 'confirmed' ? 'bad' : row.decision === 'rejected' ? 'ok' : undefined}>
                          {decisionLabel(row.decision)}
                        </Tag>
                      </td>
                      <td>
                        <Tag tone={severityTone(row.severity)}>{severityLabel(row.severity)}</Tag>
                      </td>
                      <td>{row.source_domain}</td>
                      <td>
                        <IdChip value={row.infringement_id} what="match ID" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

function TaskCard({
  task,
  pending,
  onDecide,
}: {
  task: ReviewTask;
  pending: boolean;
  onDecide: (decision: ReviewDecision, severity?: string) => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [override, setOverride] = useState('');
  const triage = Object.entries(task.triage ?? {}).filter(([key]) => key !== 'image_url');

  return (
    <>
      <dl className="cr-kv">
        <dt>Severity</dt>
        <dd>
          <Tag tone={severityTone(task.severity)}>{severityLabel(task.severity)}</Tag>
        </dd>
        <dt>Website</dt>
        <dd>{task.source_domain}</dd>
        <dt>Page</dt>
        <dd>
          <a href={task.page_url} target="_blank" rel="noopener noreferrer">
            Open the page in a new tab ↗
          </a>
          <div className="cr-fine" style={{ overflowWrap: 'anywhere' }}>
            {task.page_url}
          </div>
        </dd>
        <dt>Image address</dt>
        <dd>
          <span className="cr-mono" style={{ overflowWrap: 'anywhere' }}>
            {task.image_url ?? '—'}
          </span>
          <div className="cr-fine">Shown as text only — the console never opens or displays match images.</div>
        </dd>
        <dt>Face match</dt>
        <dd>{show(task.face_match_score)}</dd>
        <dt>Person</dt>
        <dd>
          <IdChip value={task.user_ref} href={adminHash('scores', task.user_ref)} what="person ID" />
        </dd>
        <dt>Match</dt>
        <dd>
          <IdChip value={task.infringement_id} what="match ID" />
        </dd>
      </dl>

      {triage.length > 0 && (
        <>
          <h3>Assessment details</h3>
          <dl className="cr-kv">
            {triage.map(([key, value]) => (
              <div key={key} style={{ display: 'contents' }}>
                <dt>{TRIAGE_LABELS[key] ?? humanise(key)}</dt>
                <dd>{show(value)}</dd>
              </div>
            ))}
          </dl>
        </>
      )}

      <div className="cr-actions" style={{ marginTop: 18 }}>
        <button type="button" className="cr-btn primary" disabled={pending} onClick={() => setConfirming(true)}>
          Confirm…
        </button>
        <button type="button" className="cr-btn danger" disabled={pending} onClick={() => onDecide('rejected')}>
          {pending ? 'Working…' : 'Reject'}
        </button>
        <button type="button" className="cr-btn ghost" disabled={pending} onClick={() => onDecide('uncertain')}>
          Uncertain — leave it in the queue
        </button>
      </div>

      {confirming && (
        <div className="cr-confirm" role="group" aria-label="Confirm this match">
          <strong>Confirming makes this match visible to the person in the photo.</strong>
          <span className="cr-muted">
            It appears in their report as a finding and their protection score is recalculated. Please be
            sure before you continue.
          </span>
          <label className="cr-field">
            <span>
              Severity <small>leave as assessed, or change it</small>
            </span>
            <select value={override} onChange={(event) => setOverride(event.target.value)}>
              <option value="">Keep: {severityLabel(task.severity)}</option>
              {REVIEW_SEVERITIES.map((severity) => (
                <option key={severity} value={severity}>
                  {severityLabel(severity)}
                </option>
              ))}
            </select>
          </label>
          <div className="cr-actions">
            <button
              type="button"
              className="cr-btn danger"
              disabled={pending}
              onClick={() => {
                onDecide('confirmed', override === '' ? undefined : override);
                setConfirming(false);
              }}
            >
              {pending ? 'Confirming…' : `Confirm as “${severityLabel(override === '' ? task.severity : override)}”`}
            </button>
            <button type="button" className="cr-btn ghost" onClick={() => setConfirming(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}
